import { globalEventBus, GameEvents } from '../core/EventBus.js';

/**
 * 吸附系统
 * 处理拼图块靠近正确位置时的自动吸附
 * 以及相邻拼图块之间的吸附
 */
export class SnapSystem {
    constructor(config = {}) {
        this.config = {
            threshold: config.threshold || 15,  // 吸附距离阈值
            enabled: config.enabled !== false   // 是否启用吸附
        };

        this.events = globalEventBus;
    }

    /**
     * 检查拼图块是否应该吸附到正确位置
     * @param {Piece} piece 要检查的拼图块
     * @returns {boolean} 是否应该吸附
     */
    checkSnap(piece, thresholdOverride = null) {
        if (!this.config.enabled || piece.isSnapped) {
            return false;
        }

        return piece.isNearCorrectPosition(thresholdOverride || this.config.threshold);
    }

    /**
     * 检查两个碎片是否是相邻的
     * @param {Piece} piece1 碎片1
     * @param {Piece} piece2 碎片2
     * @returns {string|null} 相邻方向 'left' | 'right' | 'top' | 'bottom' | null
     */
    getAdjacentDirection(piece1, piece2) {
        const colDiff = piece2.col - piece1.col;
        const rowDiff = piece2.row - piece1.row;

        // 必须是直接相邻（上下左右），不能是对角
        if (colDiff === 1 && rowDiff === 0) return 'right';
        if (colDiff === -1 && rowDiff === 0) return 'left';
        if (colDiff === 0 && rowDiff === 1) return 'bottom';
        if (colDiff === 0 && rowDiff === -1) return 'top';

        return null;
    }

    /**
     * 检查碎片是否可以与相邻碎片吸附 (O(1) 优化版)
     * @param {Piece} piece 当前碎片
     * @param {Map<string, Piece>} pieceMap 逻辑坐标映射表
     * @param {number} pieceSize 碎片基础尺寸
     * @param {Set<Piece>} excludeGroup 需要排除的碎片组（通常是自身所在的组）
     * @returns {Object|null} { targetPiece, direction, snapX, snapY } 或 null
     */
    checkNeighborSnap(piece, pieceMap, pieceSize, excludeGroup = null, thresholdOverride = null) {
        if (!this.config.enabled || piece.isSnapped) {
            return null;
        }

        const piecePos = piece.getPosition();
        const threshold = thresholdOverride || this.config.threshold;
        const { col, row } = piece;

        // 定义四个可能的邻居逻辑坐标
        const neighbors = [
            { c: col + 1, r: row, dir: 'right' },
            { c: col - 1, r: row, dir: 'left' },
            { c: col, r: row + 1, dir: 'bottom' },
            { c: col, r: row - 1, dir: 'top' }
        ];

        for (const n of neighbors) {
            // 1. 直接通过 Map 查找邻居是否存在
            const key = `${n.c},${n.r}`;
            const otherPiece = pieceMap.get(key);

            if (!otherPiece) {
                continue;
            }

            // 2. 排除自身组内的碎片
            if (excludeGroup && excludeGroup.has(otherPiece)) {
                continue;
            }

            // 3. 检查邻居旋转是否一致 (允许非0度吸附，只要角度相同)
            const rotDiff = Math.abs(piece.rotation - otherPiece.rotation);
            // 处理 360 度循环 (e.g. 0 和 360)
            const normalizedDiff = rotDiff > 180 ? 360 - rotDiff : rotDiff;

            if (normalizedDiff > 5) {
                console.log(`[SnapDebug] ${col},${row} → ${key}: ROTATION MISMATCH (piece=${piece.rotation.toFixed(1)}°, other=${otherPiece.rotation.toFixed(1)}°, diff=${normalizedDiff.toFixed(1)}°)`);
                continue;
            }

            // 4. 计算期望的物理位置
            const otherPos = otherPiece.getPosition();

            // 基础相对偏移 (0度时 piece 相对于 otherPiece 的位置)
            let baseOffsetX = 0;
            let baseOffsetY = 0;

            switch (n.dir) {
                case 'right': // piece is left of neighbor (other)
                    baseOffsetX = -pieceSize;
                    break;
                case 'left':  // piece is right of neighbor
                    baseOffsetX = pieceSize;
                    break;
                case 'bottom': // piece is above neighbor
                    baseOffsetY = -pieceSize;
                    break;
                case 'top':    // piece is below neighbor
                    baseOffsetY = pieceSize;
                    break;
            }

            // 应用旋转变换
            // 如果两个碎片都旋转了 90 度，它们的相对位置也会旋转 90 度
            const rad = piece.rotation * (Math.PI / 180);
            const rotatedOffsetX = baseOffsetX * Math.cos(rad) - baseOffsetY * Math.sin(rad);
            const rotatedOffsetY = baseOffsetX * Math.sin(rad) + baseOffsetY * Math.cos(rad);

            const expectedX = otherPos.x + rotatedOffsetX;
            const expectedY = otherPos.y + rotatedOffsetY;

            // 5. 检查物理距离
            const dx = Math.abs(piecePos.x - expectedX);
            const dy = Math.abs(piecePos.y - expectedY);

            if (dx < threshold && dy < threshold) {
                console.log(`[SnapDebug] ${col},${row} → ${key}: SNAP OK (dx=${dx.toFixed(1)}, dy=${dy.toFixed(1)})`);
                return {
                    targetPiece: otherPiece,
                    direction: n.dir,
                    snapX: expectedX,
                    snapY: expectedY
                };
            } else {
                console.log(`[SnapDebug] ${col},${row} → ${key}: DIST FAIL (dx=${dx.toFixed(1)}, dy=${dy.toFixed(1)}, threshold=${threshold})`);
            }
        }

        return null;
    }

    /**
     * 检查多个拼图块的吸附
     * @param {Array<Piece>} pieces 拼图块数组
     * @returns {Array<Piece>} 应该吸附的拼图块
     */
    checkSnapMultiple(pieces) {
        return pieces.filter(piece => this.checkSnap(piece));
    }

    /**
     * 更新配置
     * @param {Object} config 新配置
     */
    updateConfig(config) {
        this.config = { ...this.config, ...config };
    }

    /**
     * 启用吸附
     */
    enable() {
        this.config.enabled = true;
    }

    /**
     * 禁用吸附
     */
    disable() {
        this.config.enabled = false;
    }

    /**
     * 设置吸附阈值
     * @param {number} threshold 新阈值
     */
    setThreshold(threshold) {
        this.config.threshold = threshold;
    }

    /**
     * 获取吸附阈值
     * @returns {number} 当前阈值
     */
    getThreshold() {
        return this.config.threshold;
    }

    /**
     * 检查是否启用
     * @returns {boolean} 是否启用
     */
    isEnabled() {
        return this.config.enabled;
    }
}

export default SnapSystem;
